import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable max-lines */
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import StyleIcon from '@mui/icons-material/Style';
import { Avatar, Card, CardContent, CardHeader, Divider, Fab, IconButton, List, ListItem, ListItemIcon, ListItemText, } from '@mui/material';
import { useEffect, useState } from 'react';
import { readDynamicConfig, writeDynamicConfig, } from '@zurich/common/config';
import { useClientFlagBag } from '@zurich/common/featureFlags';
import { ThemeType } from '@zurich/common/types';
import { styled } from '../../utils';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { FormFlex } from '../FormGrid';
import { MultiSelect } from '../MultiSelect';
import { Select } from '../Select';
import { Typography } from '../Typography';
import { FeatureFlagsOverlay } from './FeatureFlags/FeatureFlagsOverlay';
import { ReleaseNotesOverlay } from './ReleaseNotes/ReleaseNotesOverlay';
export function DebugHelper({ config, userGroups, userEffectiveGroups, onSignOut, }) {
    const [releaseInfo, setReleaseInfo] = useState(null);
    const [menuVisible, setMenuVisible] = useState(false);
    const [featureFlagsVisible, setFeatureFlagsVisible] = useState(false);
    const clientFlagBag = useClientFlagBag();
    const activeFeatureFlags = Object.keys(clientFlagBag).length;
    const [devOptionsVisible, setDevOptionsVisible] = useState(!getDevOptionsLocked());
    const [releaseNotesVisible, setReleaseNotesVisible] = useState(false);
    const [dynamicConfig, setDynamicConfig] = useState(null);
    const [activeDynamicConfig, setActiveDynamicConfig] = useState(dynamicConfig);
    const [dynamicConfigApplied, setDynamicConfigApplied] = useState(false);
    // debug helper is active if feature flags are set or roles are mocked
    const active = activeFeatureFlags > 0 ||
        (dynamicConfig !== null && Object.keys(dynamicConfig).length > 0);
    function getDevOptionsLocked() {
        return (typeof window !== 'undefined' &&
            localStorage.getItem('devOptionsLocked') !== '0');
    }
    useEffect(() => {
        if (activeDynamicConfig === null) {
            const dynamicConfig = readDynamicConfig(config.appName);
            setActiveDynamicConfig(dynamicConfig);
            setDynamicConfig(dynamicConfig);
        }
    }, [activeDynamicConfig, config.appName]);
    useEffect(() => {
        if (config.debugHelper.releaseFilePath) {
            fetch(`${config.debugHelper.releaseFilePath}?_=${Date.now()}`)
                .then((res) => res.json())
                .then((info) => setReleaseInfo(info))
                .catch((e) => {
                console.warn('Could not fetch release information file', e);
            });
        }
        else if (config.buildVersion === 'local') {
            setReleaseInfo({ version: '', stage: 'LOCAL' });
            setDevOptionsVisible(true);
        }
        else {
            setReleaseInfo({ version: 'n/a', stage: '' });
        }
    }, [config]);
    if (!config.debug || !activeDynamicConfig || !dynamicConfig || !releaseInfo) {
        return null;
    }
    const lockDevOptions = (locked = true) => {
        if (typeof window !== 'undefined') {
            localStorage.setItem('devOptionsLocked', locked ? '1' : '0');
        }
        setDevOptionsVisible(!locked);
        // Reset dynamic config if the developer options are locked
        if (locked) {
            applyDynamicConfig(null, true);
        }
    };
    const handleFabClick = () => {
        setMenuVisible(true);
    };
    const hideMenu = () => {
        setMenuVisible(false);
    };
    const showReleaseNotes = () => {
        setReleaseNotesVisible(true);
    };
    const hideReleaseNotes = () => {
        setReleaseNotesVisible(false);
    };
    const showFeatureFlags = () => {
        setFeatureFlagsVisible(true);
    };
    const hideFeatureFlags = () => {
        setFeatureFlagsVisible(false);
    };
    const showAdoBuild = (buildId) => {
        if (buildId) {
            window.open(`https://azuredevops.at.zurich.com/Zurich/DiTRo/_build/results?buildId=${buildId}&view=results`);
        }
    };
    const setThemeType = (value) => {
        applyDynamicConfig({
            themeType: value && value.toUpperCase() in ThemeType
                ? value
                : undefined,
        });
    };
    const setPermissionSelectedTestGroupProfile = (value) => {
        applyDynamicConfig({
            selectedTestGroupProfile: value.length > 0 ? value : undefined, // convert empty string (= "Disabled") to undefined
        });
    };
    const setPermissionSelectedTestGroups = (value) => {
        applyDynamicConfig({
            selectedTestGroups: value.length > 0 ? value : undefined,
        });
    };
    const setPermissionSelectedTestOrganisationUnits = (value) => {
        applyDynamicConfig({
            selectedTestOrganisationUnits: value.length > 0 ? value : [],
        });
    };
    const getPermissionSelectedTestGroupsHelperText = () => {
        const groups = Array.isArray(config.permission.selectedTestGroups) &&
            config.permission.selectedTestGroups.length > 0
            ? config.permission.selectedTestGroups
            : userGroups;
        const effectiveGroups = groups?.filter((group) => userEffectiveGroups?.includes(group)) ?? [];
        return groups
            ? `Active Groups: ${groups.length}${userEffectiveGroups ? ` (${effectiveGroups.length} effective)` : ''}`
            : 'No groups available!';
    };
    const applyDynamicConfig = (partialConfig, reload = false, forceReload = false, write = false) => {
        // Spread and set the new dynamic config
        const newConfig = partialConfig
            ? { ...dynamicConfig, ...partialConfig }
            : {};
        const configChanged = JSON.stringify(newConfig) !== JSON.stringify(activeDynamicConfig);
        setDynamicConfig(newConfig);
        if (write) {
            writeDynamicConfig(config.appName, newConfig);
        }
        // reload the page to really apply the dynamic configuration overrides?
        if (!reload || (!forceReload && !configChanged)) {
            // no reload requested or no changes made
            return;
        }
        // reload/signout
        setDynamicConfigApplied(true);
        // signout - if the callback returns true, the reload was handled by the signout
        if (config.debugHelper.permissions && onSignOut) {
            if (onSignOut())
                return;
        }
        window.location.reload();
    };
    // TODO: Remove if this comment older then 7days. This only for backward compatibility. If old session has a string instead of Array<string>
    const selectedTestGroupProfile = Array.isArray(dynamicConfig.selectedTestGroupProfile)
        ? dynamicConfig.selectedTestGroupProfile
        : dynamicConfig.selectedTestGroupProfile
            ? [dynamicConfig.selectedTestGroupProfile]
            : [];
    const releaseNotesUrl = releaseInfo.notesFilename &&
        `/${releaseInfo.notesFilename}?v=${releaseInfo.version}`;
    const activeMocking = [
        ...(config.mocking.apiSources && config.mocking.apiSources.length > 0
            ? [`API (${config.mocking.apiSources.join(',')})`]
            : []),
        ...(config.mocking.session ? ['Session'] : []),
    ].join(' & ');
    return menuVisible ? (_jsxs(_Fragment, { children: [releaseNotesVisible && releaseNotesUrl && (_jsx(ReleaseNotesOverlay, { dataUrl: releaseNotesUrl, onClose: hideReleaseNotes })), featureFlagsVisible && (_jsx(FeatureFlagsOverlay, { onClose: hideFeatureFlags })), _jsxs(MenuCardStyled, { children: [_jsx(CardHeader, { avatar: _jsx(Avatar, { "aria-label": "icon", sx: {
                                backgroundColor: (theme) => theme.palette.primary.main,
                            }, children: _jsx(BugReportIcon, {}) }), title: releaseInfo.stage, subheader: releaseInfo.version, action: _jsx(IconButton, { "aria-label": "settings", onClick: hideMenu, size: "medium", children: _jsx(CloseIcon, {}) }) }), _jsxs(CardContent, { sx: {
                            padding: 0,
                        }, children: [_jsxs(List, { component: "nav", "aria-labelledby": "nested-list-subheader", children: [releaseInfo.notesFilename && (_jsxs(ListItem, { button: true, onClick: showReleaseNotes, children: [_jsx(ListItemIcon, { children: _jsx(LibraryBooksIcon, {}) }), _jsx(ListItemText, { primary: "Release Notes" })] })), config.buildVersion && config.buildVersion !== 'local' && (_jsxs(ListItem, { button: true, onClick: () => showAdoBuild(config.buildVersion), children: [_jsx(ListItemIcon, { children: _jsx(BuildIcon, {}) }), _jsx(ListItemText, { primary: `Build ${config.buildVersion}` })] })), config.debugHelper.styleGuideUrl && (_jsxs(ListItem, { button: true, component: "a", href: config.debugHelper.styleGuideUrl, target: "_blank", rel: "noreferrer", children: [_jsx(ListItemIcon, { children: _jsx(StyleIcon, {}) }), _jsx(ListItemText, { primary: "Style Guide" })] })), _jsxs(ListItem, { button: true, onClick: showFeatureFlags, children: [_jsx(ListItemIcon, { children: _jsx(Badge, { badgeContent: activeFeatureFlags, color: "primary", children: _jsx(FlagIcon, {}) }) }), _jsx(ListItemText, { primary: "Feature Flags" })] }), config.buildVersion !== 'local' && (_jsx(ListItem, { button: true, onClick: () => lockDevOptions(devOptionsVisible), children: !devOptionsVisible ? (_jsxs(_Fragment, { children: [_jsx(ListItemIcon, { children: _jsx(LockOpenIcon, {}) }), _jsx(ListItemText, { primary: "Unlock Developer Options", secondary: "Don't use for testing!" })] })) : (_jsxs(_Fragment, { children: [_jsx(ListItemIcon, { children: _jsx(LockIcon, {}) }), _jsx(ListItemText, { primary: "Lock Developer Options", secondary: "Page will reload if necessary" })] })) }))] }), devOptionsVisible && (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(FormContainerStyled, { children: [_jsx(Select, { value: dynamicConfig.themeType ?? 'default', onChange: (value) => setThemeType(value), helperText: config.themeType
                                                    ? `Active: ${config.themeType}`
                                                    : 'Defined by App', options: [
                                                    {
                                                        label: 'Default',
                                                        value: 'default',
                                                    },
                                                    {
                                                        label: ThemeType.ZURICH,
                                                        value: ThemeType.ZURICH,
                                                    },
                                                    {
                                                        label: ThemeType.ZC,
                                                        value: ThemeType.ZC,
                                                    },
                                                ], label: "Theme" }), config.debugHelper.permissions && (_jsx(MultiSelect, { value: selectedTestGroupProfile, multiple: true, onChange: (value) => {
                                                    setPermissionSelectedTestGroupProfile(value);
                                                }, options: config.permission.testGroupProfiles, label: "RBAC Rollen", renderValue: (selected) => {
                                                    if (selected.length === 0)
                                                        return 'Default';
                                                    if (selected.length === 1)
                                                        return selected[0];
                                                    return `${selected.length} Roles selected`;
                                                } })), config.debugHelper.permissions &&
                                                config.permission.testOrganisationUnits.length > 0 && (_jsx(MultiSelect, { value: dynamicConfig.selectedTestOrganisationUnits ?? [], onChange: (value) => setPermissionSelectedTestOrganisationUnits(value), multiple: true, options: config.permission.testOrganisationUnits, label: "Organisationsgruppen", renderValue: (selected) => {
                                                    if (selected.length === 0)
                                                        return 'Default';
                                                    if (selected.length === 1)
                                                        return selected[0];
                                                    return `${selected.length} gruppen ausgewählt`;
                                                } })), config.debugHelper.permissions && (_jsx(MultiSelect, { value: dynamicConfig.selectedTestGroups ?? [], onChange: (value) => setPermissionSelectedTestGroups(value), helperText: getPermissionSelectedTestGroupsHelperText(), multiple: true, options: config.permission.testGroups, label: "Okta Gruppen", renderValue: (selected) => {
                                                    if (selected.length === 0)
                                                        return 'Default';
                                                    if (selected.length === 1)
                                                        return selected[0];
                                                    return `${selected.length} gruppen ausgewählt`;
                                                } })), _jsxs(FormFlex, { justifyContent: "center", rowGap: 1, columnGap: 1, children: [_jsx(Button, { color: "primary", variant: "contained", size: "small", onClick: () => applyDynamicConfig(null), children: "Reset" }), _jsx(Button, { color: "primary", variant: "contained", size: "small", onClick: () => applyDynamicConfig(dynamicConfig, true, true, true), loading: dynamicConfigApplied, children: dynamicConfigApplied ? (_jsx(_Fragment, { children: "Reloading" })) : (_jsx(_Fragment, { children: "Apply & Reload" })) })] })] })] }))] })] })] })) : (_jsxs(FabStyled, { color: activeMocking.length > 0 ? 'warning' : active ? 'primary' : 'secondary', variant: "extended", size: "small", onClick: handleFabClick, children: [activeMocking.length > 0 ? (_jsxs("b", { children: ["Mocking Active: ", activeMocking, " "] })) : null, _jsx(BugReportIcon, {}), releaseInfo.version && (_jsx(Typography, { fontSize: "0.8rem", variant: "inherit", component: "span", sx: {
                    paddingLeft: (theme) => theme.spacing(1),
                }, children: releaseInfo.version }))] }));
}
const FabStyled = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 5,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
}));
const MenuCardStyled = styled(Card)(({ theme }) => ({
    position: 'fixed',
    width: '100%',
    maxWidth: 320,
    zIndex: 10,
    right: theme.spacing(2),
    bottom: theme.spacing(2),
}));
const FormContainerStyled = styled('div')(({ theme }) => ({
    padding: theme.spacing(2, 4, 0),
    display: 'grid',
    gap: theme.spacing(2),
}));
